import React, { useState, useEffect } from 'react';
import { MapPin, Edit2, Trash2, Plus, Save, X, AlertCircle, CheckCircle, Download } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';

// US States list
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function StateServiceDistributionManager() {
  const [distributions, setDistributions] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDistribution, setNewDistribution] = useState({
    state: '',
    service_id: '',
    service_name: '',
    distribution_profile: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [distResponse, servicesResponse] = await Promise.all([
        api.get('/state-service-distributions'),
        api.get('/services')
      ]);
      setDistributions(distResponse.data.distributions || []);
      setServices(servicesResponse.data.services || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load distributions');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (distribution) => {
    setEditingId(distribution.id);
    setEditData({ ...distribution });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/state-service-distributions/${editingId}`, editData);
      toast.success('Distribution updated successfully');
      setEditingId(null);
      setEditData(null);
      loadData();
    } catch (error) {
      console.error('Error updating distribution:', error);
      toast.error(error.response?.data?.detail || 'Failed to update distribution');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleDelete = async (id, state, serviceName) => {
    if (!window.confirm(`Delete distribution override for ${state} + ${serviceName}?`)) {
      return;
    }

    try {
      await api.delete(`/state-service-distributions/${id}`);
      toast.success('Distribution deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting distribution:', error);
      toast.error('Failed to delete distribution');
    }
  };

  const handleAddNew = () => {
    setShowAddForm(true);
    setNewDistribution({
      state: '',
      service_id: '',
      service_name: '',
      distribution_profile: []
    });
  };

  const handleServiceSelect = (serviceName) => {
    const service = services.find(s => s.name === serviceName);
    if (service) {
      setNewDistribution(prev => ({
        ...prev,
        service_id: service.id,
        service_name: service.name,
        distribution_profile: JSON.parse(JSON.stringify(service.distribution_profile || []))
      }));
    }
  };

  const handleCreateDistribution = async () => {
    // Validate
    if (!newDistribution.state || !newDistribution.service_id) {
      toast.error('Please select both state and service');
      return;
    }

    if (!newDistribution.distribution_profile || newDistribution.distribution_profile.length === 0) {
      toast.error('Distribution profile is required');
      return;
    }

    try {
      await api.post('/state-service-distributions', newDistribution);
      toast.success('Distribution override created successfully');
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating distribution:', error);
      toast.error(error.response?.data?.detail || 'Failed to create distribution');
    }
  };

  const updateDistributionTier = (index, field, value, isEditing = false) => {
    if (isEditing && editData) {
      const updated = { ...editData };
      if (field === 'pct') {
        updated.distribution_profile[index][field] = parseFloat(value);
      } else if (field === 'low' || field === 'high') {
        updated.distribution_profile[index][field] = parseFloat(value);
        // Auto-calculate midpoint
        const low = field === 'low' ? parseFloat(value) : updated.distribution_profile[index].low;
        const high = field === 'high' ? parseFloat(value) : updated.distribution_profile[index].high;
        updated.distribution_profile[index].mid = (low + high) / 2;
        // Auto-generate range label
        updated.distribution_profile[index].label = generateRangeLabel(low, high);
      }
      setEditData(updated);
    } else if (!isEditing && newDistribution) {
      const updated = { ...newDistribution };
      if (field === 'pct') {
        updated.distribution_profile[index][field] = parseFloat(value);
      } else if (field === 'low' || field === 'high') {
        updated.distribution_profile[index][field] = parseFloat(value);
        // Auto-calculate midpoint
        const low = field === 'low' ? parseFloat(value) : updated.distribution_profile[index].low;
        const high = field === 'high' ? parseFloat(value) : updated.distribution_profile[index].high;
        updated.distribution_profile[index].mid = (low + high) / 2;
        // Auto-generate range label
        updated.distribution_profile[index].label = generateRangeLabel(low, high);
      }
      setNewDistribution(updated);
    }
  };

  const handleExportCSV = () => {
    try {
      const rows = [];

      // ── Header row ──
      rows.push([
        'Service Name',
        'State',
        'Distribution Type',
        'Range Label',
        'Range Low ($)',
        'Range High ($)',
        'Midpoint ($)',
        'Percentage (%)',
        'Active',
        'Last Updated',
      ]);

      // ── Section 1: Default service distributions ──
      services.forEach(svc => {
        const profile = svc.distribution_profile || [];
        if (profile.length === 0) {
          rows.push([svc.name, '', 'Default', '', '', '', '', '', svc.active ? 'Yes' : 'No', svc.updated_at ? svc.updated_at.slice(0, 10) : svc.created_at?.slice(0, 10) || '']);
        } else {
          profile.forEach(tier => {
            const lo = tier.low ?? '';
            const hi = tier.high ?? '';
            const label = tier.label || (Number(lo) <= 10 ? `≤ ${hi}` : `${lo}-${hi}`);
            rows.push([
              svc.name, '', 'Default', label, lo, hi,
              tier.mid ?? '', tier.pct != null ? (tier.pct * 100).toFixed(4) : '',
              svc.active ? 'Yes' : 'No',
              svc.updated_at ? svc.updated_at.slice(0, 10) : svc.created_at?.slice(0, 10) || '',
            ]);
          });
        }
      });

      // ── Section 2: State-specific overrides ──
      distributions.forEach(dist => {
        const profile = dist.distribution_profile || [];
        if (profile.length === 0) {
          rows.push([dist.service_name, dist.state, 'State Override', '', '', '', '', '', dist.is_active ? 'Yes' : 'No', dist.updated_at ? dist.updated_at.slice(0, 10) : dist.created_at?.slice(0, 10) || '']);
        } else {
          profile.forEach(tier => {
            const lo = tier.low ?? '';
            const hi = tier.high ?? '';
            const label = tier.label || (Number(lo) <= 10 ? `≤ ${hi}` : `${lo}-${hi}`);
            rows.push([
              dist.service_name, dist.state, 'State Override', label, lo, hi,
              tier.mid ?? '', tier.pct != null ? (tier.pct * 100).toFixed(4) : '',
              dist.is_active ? 'Yes' : 'No',
              dist.updated_at ? dist.updated_at.slice(0, 10) : dist.created_at?.slice(0, 10) || '',
            ]);
          });
        }
      });

      // ── Build CSV string ──
      const escape = val => {
        const s = String(val ?? '');
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = rows.map(row => row.map(escape).join(',')).join('\r\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GW_Service_Distributions_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length - 1} rows (${services.length} services + ${distributions.length} state overrides)`);
    } catch (err) {
      console.error('CSV export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  const generateRangeLabel = (low, high) => {
    // If low is 0 or very small (≤ 10), use "≤ X" format
    if (low <= 10) {
      return `≤ ${high}`;
    }
    // Otherwise use "X-Y" format
    return `${low}-${high.toLocaleString()}`;
  };

  const calculateTotalPercentage = (profile) => {
    if (!profile || profile.length === 0) return 0;
    return profile.reduce((sum, tier) => sum + (tier.pct || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading distributions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-indigo-600" />
            State-Service Distribution Overrides
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Override default service distributions for specific state combinations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            data-testid="export-distributions-csv-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium text-sm"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Override
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white border-2 border-indigo-200 rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Create New Distribution Override</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* State Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                State <span className="text-red-600">*</span>
              </label>
              <select
                value={newDistribution.state}
                onChange={(e) => setNewDistribution({ ...newDistribution, state: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select State...</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Service Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Service <span className="text-red-600">*</span>
              </label>
              <SearchableSelect
                options={services.map(s => s.name)}
                value={newDistribution.service_name}
                onChange={handleServiceSelect}
                placeholder="Select service..."
                searchPlaceholder="Search services..."
              />
            </div>
          </div>

          {/* Distribution Profile Editor */}
          {newDistribution.distribution_profile.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Distribution Profile (must sum to 100%)
              </label>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 pb-2 border-b border-slate-300">
                  <div className="col-span-3">Collection Range (auto)</div>
                  <div className="col-span-2 text-center">Low ($)</div>
                  <div className="col-span-2 text-center">Mid ($) (auto)</div>
                  <div className="col-span-2 text-center">High ($)</div>
                  <div className="col-span-3 text-center">Percentage (%)</div>
                </div>
                
                {newDistribution.distribution_profile.map((tier, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3">
                      <input
                        type="text"
                        value={tier.label}
                        readOnly
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-slate-100 text-slate-600"
                        placeholder="Auto-generated"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        value={tier.low}
                                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                        onChange={(e) => updateDistributionTier(index, 'low', parseFloat(e.target.value) || 0, false)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        value={tier.mid}
                        readOnly
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-slate-100 text-slate-600"
                        title="Auto-calculated as (Low + High) / 2"
                                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        value={tier.high}
                                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                        onChange={(e) => updateDistributionTier(index, 'high', parseFloat(e.target.value) || 0, false)}
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div className="col-span-3 flex items-center gap-2">
                      <input
                        type="number"
                        value={(tier.pct * 100).toFixed(2)}
                                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                        onChange={(e) => updateDistributionTier(index, 'pct', parseFloat(e.target.value) / 100, false)}
                        className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded"
                        step="0.01"
                        min="0"
                        max="100"
                      />
                      <span className="text-xs text-slate-600">%</span>
                    </div>
                  </div>
                ))}
                
                <div className="flex items-center justify-between pt-2 border-t border-slate-300">
                  <span className="text-sm font-medium text-slate-700">Total Percentage:</span>
                  <span className={`text-sm font-bold ${
                    Math.abs(calculateTotalPercentage(newDistribution.distribution_profile) - 1.0) < 0.0001
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {(calculateTotalPercentage(newDistribution.distribution_profile) * 100).toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleCreateDistribution}
              className="btn-primary flex items-center gap-2"
              disabled={!newDistribution.state || !newDistribution.service_id}
            >
              <Save className="w-4 h-4" />
              Create Override
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Distributions Table */}
      {distributions.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
          <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 mb-4">No state-service distribution overrides yet</p>
          <button onClick={handleAddNew} className="btn-primary">
            Add First Override
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">State</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Tiers</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {distributions.map((dist) => {
                const isEditing = editingId === dist.id;
                const displayData = isEditing ? editData : dist;

                return (
                  <tr key={dist.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="font-semibold text-slate-900">{displayData.state}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-700">{displayData.service_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="space-y-2 max-w-2xl">
                          <div className="grid grid-cols-5 gap-1 text-xs font-semibold text-slate-600 mb-1">
                            <div>Range (auto)</div>
                            <div className="text-center">Low</div>
                            <div className="text-center">Mid (auto)</div>
                            <div className="text-center">High</div>
                            <div className="text-center">%</div>
                          </div>
                          {displayData.distribution_profile.map((tier, index) => (
                            <div key={index} className="grid grid-cols-5 gap-1 text-xs items-center">
                              <input
                                type="text"
                                value={tier.label}
                                readOnly
                                className="px-1 py-0.5 text-xs border border-slate-300 rounded bg-slate-100 text-slate-600"
                                title="Auto-generated from Low and High"
                              />
                              <input
                                type="number"
                                value={tier.low}
                                            onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                onChange={(e) => updateDistributionTier(index, 'low', parseFloat(e.target.value) || 0, true)}
                                className="px-1 py-0.5 text-xs border border-slate-300 rounded text-center"
                                step="0.01"
                              />
                              <input
                                type="number"
                                value={tier.mid}
                                readOnly
                                className="px-1 py-0.5 text-xs border border-slate-300 rounded text-center bg-slate-100 text-slate-600"
                                title="Auto-calculated: (Low + High) / 2"
                                            onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                              />
                              <input
                                type="number"
                                value={tier.high}
                                            onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                onChange={(e) => updateDistributionTier(index, 'high', parseFloat(e.target.value) || 0, true)}
                                className="px-1 py-0.5 text-xs border border-slate-300 rounded text-center"
                                step="0.01"
                              />
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  value={(tier.pct * 100).toFixed(2)}
                                              onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                  onChange={(e) => updateDistributionTier(index, 'pct', parseFloat(e.target.value) / 100, true)}
                                  className="w-14 px-1 py-0.5 text-xs border border-slate-300 rounded"
                                  step="0.01"
                                />
                                <span className="text-xs">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-600">
                          {displayData.distribution_profile?.length || 0} tiers configured
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {displayData.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                          <AlertCircle className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleSaveEdit}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-slate-600 hover:bg-slate-100 rounded"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(dist)}
                            className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(dist.id, dist.state, dist.service_name)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
